import { UpdateFacilityPatch } from 'es/app/__generated__/global';
import compare from 'es/app/business-logic/compare';
import { IFacility, IFacilityDetails } from 'es/app/business-logic/domain-models/Facility';
import { ICompany } from 'es/app/business-logic/domain-models/ICompany';
import Site from 'es/app/business-logic/domain-models/Site';
import { ILoggedInUser } from 'es/app/business-logic/domain-models/User';
import * as FacilityService from 'es/app/business-logic/services/facility';
import { ICreateFacilityResult } from 'es/app/business-logic/services/facility';
import IAccessControlSet, {
    AccessControlCollection,
    challenge,
    ChallengeType,
} from 'es/app/security/IAccessControlSet';
import Authenticator from 'es/app/security/protected/Authenticator';
import { getSiteFromUrl, isSiteRoute } from 'es/app/views/site/RouteMap';
import { safeNavigate } from 'es/core/helpers/safeNavigate';
import LogManager from 'es/core/logging/LogManager';
import Guid from 'es/core/types/Guid';
import LocalTime from 'es/core/types/LocalTime';
import i18n from 'i18next';
import { action, autorun, computed, makeObservable, observable } from 'mobx';
import { computedFn } from 'mobx-utils';

const logger = LogManager.getLogger('app.store.UserStore');

const STORAGE_KEY = 'active_site';

export default class UserStore {
    constructor() {
        makeObservable<
            UserStore,
            '_details' | '_accessibleCompanies' | '_accessibleFacilities' | '_activeSite' | '_accessControls'
        >(this, {
            _details: observable,
            _accessibleCompanies: observable.shallow,
            _accessibleFacilities: observable.shallow,
            _activeSite: observable,
            _accessControls: observable.shallow,
            loggedInUser: computed,
            accessibleCompanies: computed,
            accessibleFacilities: computed,
            activeSite: computed,
            activeFacility: computed({ keepAlive: true }),
            setLoggedInUser: action.bound,
            setAccessibleFacilities: action.bound,
            updateFacility: action.bound,
            createFacility: action.bound,
            setActiveFacility: action.bound,
        });
        autorun(() => this.syncUrlToActiveSite());
    }

    private _details: ILoggedInUser | undefined = undefined;

    private _accessibleCompanies: ICompany[] | undefined = undefined;

    private _accessibleFacilities: IFacility[] | undefined = undefined;

    /**
     * The currently active site.
     */
    private _activeSite: Site | undefined = undefined;

    /**
     * Access controls for the user at an auth domain.
     */
    private _accessControls = new Map<Site, IAccessControlSet>();

    public get loggedInUser() {
        return this._details;
    }
    public get accessibleCompanies() {
        return this._accessibleCompanies;
    }
    public get accessibleFacilities() {
        return this._accessibleFacilities;
    }
    public get activeSite() {
        return this._activeSite;
    }

    public get activeFacility() {
        const facility = this._activeSite
            ? this._accessibleFacilities?.find(facility => facility.site === this._activeSite)
            : null;
        if (!facility) {
            throw new Error('Failed to get active facility');
        }
        return facility;
    }

    public setLoggedInUser(user: ILoggedInUser) {
        this._details = user;

        if (user?.locale && i18n.language !== user.locale) {
            i18n.changeLanguage(user.locale);
        }
    }

    public setAccessibleFacilities(companies: ICompany[], facilities: IFacility[]) {
        if (!(companies && facilities)) return;
        if (this._accessibleCompanies === companies && this._accessibleFacilities === facilities) return;

        let activeFacility =
            facilities && this._activeSite && facilities.find(facility => facility.site === this._activeSite);
        if (!activeFacility) {
            const activeFacilityAlias = UserStore.getActiveSiteFromUrl() || localStorage.getItem(STORAGE_KEY);
            if (activeFacilityAlias) {
                activeFacility = facilities.find(
                    facility => facility.site.toString() === activeFacilityAlias.toString()
                );
            }
        }

        this._accessibleCompanies = companies.toSorted(compare.byName);
        this._accessibleFacilities = facilities.toSorted(
            compare.byStringValue<IFacility>(facility => facility.site.key)
        );
        this._activeSite = activeFacility?.site;
    }

    public async updateFacility(
        facilityId: Guid,
        originalCompanyAlias: string,
        patch: UpdateFacilityPatch
    ): Promise<boolean> {
        // Parse empty strings as null
        const isSuccess = await FacilityService.updateFacility(facilityId, originalCompanyAlias, patch);
        const { companies, facilities } = await FacilityService.fetchAccessibleSites();
        if (isSuccess) {
            this.setAccessibleFacilities(companies, facilities);
        }

        return isSuccess;
    }

    public async createFacility(company: ICompany, facility: IFacilityDetails): Promise<ICreateFacilityResult> {
        const result = await FacilityService.createFacility(company, facility);

        let updatedCompanies = [...(this.accessibleCompanies ?? [])];

        const isNewCompany = !updatedCompanies.find(c => c.id === result.companyId);
        if (isNewCompany) {
            updatedCompanies = [
                ...updatedCompanies,
                {
                    id: result.companyId,
                    name: company.name,
                    alias: company.alias,
                },
            ];
        }

        const updatedFacilities = [
            ...(this.accessibleFacilities ?? []),
            {
                id: result.facilityId ?? '',
                name: facility.name,
                companyId: result.companyId,
                companyName: company.name,
                ianaTimeZoneId: facility.ianaTimeZoneId,
                windowsTimeZoneId: facility.windowsTimeZoneId,
                site: Site.get(company.alias, facility.alias),
                passwordResetFrequencyDays: facility.passwordResetFrequencyDays,
                smaxCustomerCode: facility.smaxCustomerCode,
                salesForceAccountId: facility.salesForceAccountId,
                facilityCode: result.facilityCode,
                smaxStaleDataTimeoutSeconds: facility.smaxStaleDataTimeoutSeconds,
                latitude: facility.latitude,
                longitude: facility.longitude,
                defaultZoomLevel: facility.defaultZoomLevel,
                region: facility.region,
                processDataRetentionInDays: facility.processDataRetentionInDays,
                type: facility.type,
                isActive: facility.isActive,
                useImperialUnits: facility.useImperialUnits,
            },
        ];

        this.setAccessibleFacilities(updatedCompanies, updatedFacilities);

        return result;
    }

    public setActiveFacility(facility: IFacility) {
        if (this._activeSite === facility.site) return;

        this._activeSite = facility.site;
        LocalTime.IanaTimeZone = facility.ianaTimeZoneId ?? undefined;

        localStorage.setItem(STORAGE_KEY, this._activeSite.toString());
    }

    public getCompanyAndFacility = computedFn((facilityId: Guid | undefined) => {
        const facility = this._accessibleFacilities?.find(f => f.id === facilityId);
        const company = facility && this._accessibleCompanies?.find(c => c.alias === facility.site.companyAlias);

        return [company, facility] as [ICompany | undefined, IFacility | undefined];
    });

    // ----- Access Controls--------------------------------------------------------------------------------------------

    public isAuthorized = computedFn(
        (requiredAccessControl: string | AccessControlCollection, site: Site, challengeType = ChallengeType.Any) => {
            const accessControls = this._accessControls.get(site);
            if (!accessControls) {
                this.loadAccessControlsForSite(site);
                return null;
            }

            const hasAnyAccessControls = Object.keys(accessControls).length > 0;
            if (!hasAnyAccessControls) {
                logger.warn(
                    `User does not have access to '${requiredAccessControl}' at ${site} - they have no access controls for this site.`
                );
                return false;
            }

            // If there's no *specific* access control required for this auth domain, then we presume the user is
            // allowed to use this route provided they have *any* access control.
            return requiredAccessControl ? challenge(accessControls, requiredAccessControl, challengeType) : true;
        }
    );

    private loadAccessControlsForSite(site: Site) {
        const facility = site !== Site.Global ? this._accessibleFacilities?.find(f => f.site === site) : null;

        // fetchAccessControls will cache the promise so we can call this as often as we like but the
        // request will only execute once.
        Authenticator.fetchAccessControls(facility)?.then(siteAccessControls => {
            if (this._accessControls.get(site) !== siteAccessControls) {
                this._accessControls.set(site, siteAccessControls);
            }
        });
    }

    private static getActiveSiteFromUrl() {
        const urlSite = getSiteFromUrl(window.location.pathname);
        return urlSite !== Site.Global ? urlSite : null;
    }

    // ----- Reactions -------------------------------------------------------------------------------------------------

    private syncUrlToActiveSite() {
        if (!this._activeSite) return;

        const { pathname } = window.location;
        if (!isSiteRoute(pathname)) return;

        const siteInUrl = getSiteFromUrl(pathname);
        if (siteInUrl === this._activeSite) return;

        const updatedLocation = (() => {
            const pathSegments = pathname.split('/');
            const isValidPath = pathSegments[1] === 'company' && pathSegments[3] === 'site';

            if (!isValidPath) return pathname;
            return [
                ...pathSegments.slice(0, 2),
                this._activeSite.companyAlias,
                ...pathSegments.slice(3, 4),
                this._activeSite.facilityAlias,
                ...pathSegments.slice(5),
            ].join('/');
        })();
        // If the user was looking at something specific (e.g. an alert or a report), that won't exist in the new
        // facility, so take them up to the parent page. Note that this may take them somewhere that doesn't make sense
        // (e.g. if they were looking at an alert and the new facility doesn't have alerts enabled) but that shouldn't
        // be too often.
        const safeRouteRegex = new RegExp(`.+${this._activeSite.facilityAlias}/[^/]+`);
        const safeRoute = safeRouteRegex.exec(updatedLocation);

        // Use safe navigation to prevent open redirect vulnerabilities
        safeNavigate(safeRoute ? safeRoute[0] : updatedLocation);
    }
}
